import { Component, OnInit, Optional, Inject, Input, Output, EventEmitter, inject } from '@angular/core';
import { MoneyInputDirective } from '../../../utils/directives/money-input.directive';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { CasheringService } from '../../../domain/services/cashering/cashering.service';
import { ReturnModel, ReturnStatus, ReturnUpdateDto } from '../../../domain/models/return.model';
import { ReturnSettingsDialogComponent, ReturnSettingsType } from './return-settings-dialog.component';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import { ExemptionType, ExemptionTypesService } from '../../../domain/services/admin/exemption-types.service';

@Component({
  selector: 'app-return-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    FormsModule,
    MoneyInputDirective,
  ],
  templateUrl: './return-detail.component.html',
  styleUrls: ['./return-detail.component.css']
})
export class ReturnDetailComponent implements OnInit {
  returnData!: ReturnModel;
  customerId!: number;
  returnId!: number;
  ReturnStatus = ReturnStatus;
  /** Values are read-only until the cashier clicks "Edit Values". */
  editMode = false;
  /** Whether the screen was opened with the intent to edit (eye / returns row). */
  private wantsEdit = false;
  /** True when rendered inside the cashering modal (vs the standalone route). */
  isDialog = false;
  saving = false;
  /** Set once a save succeeds so GO BACK tells the parent list to refresh. */
  savedDirty = false;

  /**
   * Embedded mode: rendered inline inside the Search customer Returns tab (full page,
   * not a modal and not a standalone route). The parent supplies the ids and listens
   * for {@link back} to swap its view back to the returns list.
   */
  @Input() embedded = false;
  @Input() embeddedReturnId?: number;
  @Input() embeddedCustomerId?: number;
  @Input() embeddedEdit = false;
  /** Rolled into a plan/assessment/lien: no editing, no fee changes, view only. */
  @Input() embeddedReadOnly = false;
  /** Emits the dirty flag (true if a save happened) when the user leaves the editor. */
  @Output() back = new EventEmitter<boolean>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private casheringService: CasheringService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    @Optional() private dialogRef: MatDialogRef<ReturnDetailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { returnId: number; customerId: number; edit?: boolean } | null
  ) {}

  toggleEdit(): void {
    if (this.embeddedReadOnly) { return; }
    this.editMode = !this.editMode;
    if (this.returnData) {
      this.returnData.enableCustomerEditing = this.editMode;
    }
  }

  /** Opens a return sub-section (Fee/Allowances, Notes, Attachments, History) as a modal. */
  openSettings(type: ReturnSettingsType): void {
    const ref = this.dialog.open(ReturnSettingsDialogComponent, {
      data: { type, returnData: this.returnData, returnId: this.returnId },
      width: type === 'history' || type === 'attachments' ? '820px' : '640px',
      maxWidth: '95vw',
      autoFocus: false,
      // Fees: only the dialog's own buttons close it, so a change is always reported (Escape and
      // backdrop skipped close(dirty)). Reloading on every close would drop unsaved receipt edits.
      disableClose: type === 'fees',
    });
    ref.afterClosed().subscribe((changed) => {
      if (changed) { this.loadReturnData(); }
    });
  }

  private readonly exemptionTypesService = inject(ExemptionTypesService);

  ngOnInit(): void {
    this.exemptionTypesService.active().subscribe({ next: t => this.exemptionTypes = t, error: () => {} });
    if (this.dialogData?.returnId) {
      // Opened as a modal from the cashering returns table.
      this.isDialog = true;
      this.customerId = this.dialogData.customerId;
      this.returnId = this.dialogData.returnId;
      // Cashier opened a return to edit the declared taxes — start in edit mode.
      this.wantsEdit = this.dialogData.edit ?? true;
      this.loadReturnData();
    } else if (this.embedded && this.embeddedReturnId) {
      // Rendered inline inside the Search customer Returns tab (full page).
      this.customerId = this.embeddedCustomerId!;
      this.returnId = this.embeddedReturnId;
      this.wantsEdit = this.embeddedEdit;
      this.loadReturnData();
    } else {
      this.route.params.subscribe(params => {
        this.customerId = +params['customerId'];
        this.returnId = +params['returnId'];
        this.loadReturnData();
      });
    }
  }

  loadReturnData(): void {
    this.casheringService.getReturnById(this.returnId).subscribe(
      data => {
        this.returnData = data as unknown as ReturnModel;
        // Backend sends propertyAddress; the view model uses `address`.
        if (this.returnData?.properties) {
          this.returnData.properties = this.returnData.properties.map((p: any) => ({
            ...p,
            address: p.propertyAddress ?? p.address,
          }));
        }
        // Surface per-type fee amounts (late fee, interest, etc.) for the summary rows,
        // derived from the enabled fee lines the backend returns.
        const feeList: any[] = (data as any).fees || [];
        const sumType = (t: string) => feeList
          .filter(f => f.enabled !== false && f.type === t)
          .reduce((s, f) => s + (f.amount || 0), 0);
        this.returnData.lateFee = sumType('LATE_FEE');
        this.returnData.lateInterest = sumType('LATE_INTEREST');
        this.returnData.nsfFee = sumType('NSF_FEE');
        this.returnData.attorneyFee = sumType('ATTORNEY_FEE');
        this.returnData.lienFee = sumType('LIEN_FEE');
        const labels: Record<string, string> = { NSF_FEE: 'NSF Fee', ATTORNEY_FEE: 'Attorney Fee', LIEN_FEE: 'Lien Fee', CUSTOM: 'Fee' };
        this.extraFees = feeList
          .filter(f => f.enabled !== false && f.type !== 'LATE_FEE' && f.type !== 'LATE_INTEREST' && f.amount)
          .map(f => ({ label: f.customName || labels[f.type] || f.type, amount: f.amount }));

        // Allowances were never read from the response: the row showed blank and the
        // "Fee / Allowances (n)" counter stayed 0 even though the total had them (QA 2026-10-04).
        const allowanceList: any[] = ((data as any).allowances || [])
          .filter((x: any) => x.enabled !== false && x.amount);
        this.allowanceLines = allowanceList
          .map(x => ({ label: x.customName || 'Online Collection Allowance', amount: x.amount }));
        this.chargeCount = feeList.length + ((data as any).allowances || []).length;
        // Baseline for the live total while typing: only the tax moves, the backend keeps the rest.
        this.loadedTaxes = this.returnData.totalTaxes ?? 0;
        this.loadedTotal = this.returnData.totalAmountDue ?? 0;

        // Backend DTO uses customerEditingEnabled; the view model uses enableCustomerEditing.
        this.returnData.enableCustomerEditing = (data as any).customerEditingEnabled ?? false;
        // Reflect the persisted editing flag; an explicit "open to edit" still starts on.
        this.editMode = !this.embeddedReadOnly
          && (!!this.returnData.enableCustomerEditing || this.wantsEdit);
      },
      (error: any) => {
        console.error('Error loading return data:', error);
        if (this.returnData) {
          // A refresh after a change failed: say so rather than showing stale charges.
          this.dialogService.openErrorDialog('Could not refresh', 'The change was saved, but the return could not be reloaded. Reopen the return to see it.');
        }
      }
    );
  }

  /** Fee lines other than late fee/interest (NSF, lien, attorney, custom), each on its own row. */
  extraFees: { label: string; amount: number }[] = [];
  /** Enabled allowance lines (auto collection allowance or staff-added), each on its own row. */
  allowanceLines: { label: string; amount: number }[] = [];
  /** Fee + allowance lines on the return, for the sidebar counter. */
  chargeCount = 0;
  private loadedTaxes = 0;
  private loadedTotal = 0;

  getSubtotalGross(): number {
    return this.returnData?.properties.reduce((sum, prop) => sum + prop.grossRentalReceipts, 0) || 0;
  }

  getSubtotalExemptions(): number {
    return this.returnData?.properties.reduce((sum, prop) => sum + prop.exemptions, 0) || 0;
  }

  updatePropertyValue(propertyId: number, field: 'grossRentalReceipts' | 'exemptions', value: number): void {
    const property = this.returnData.properties.find(p => p.id === propertyId);
    if (property) {
      property[field] = value;
      this.recalculateTotals();
    }
  }

  /**
   * The calculation date drives late fee / interest (computed on the backend as-of
   * that date). Persist immediately so the recomputed totals reload — otherwise
   * changing the date appeared to do nothing until a manual save.
   */
  onCalculationDateChange(): void {
    if (this.editMode) {
      this.save();
    }
  }

  recalculateTotals(): void {
    const subtotalGross = this.getSubtotalGross();
    const subtotalExemptions = this.getSubtotalExemptions();

    this.returnData.grossRentalReceipts = subtotalGross;
    this.returnData.exemptions = subtotalExemptions;
    this.returnData.taxableRentalReceipts = taxableReceipts(this.returnData.properties || []);

    // Note: Tax calculation would happen on backend
    this.updateTotalAmountDue();
  }

  /**
   * Live preview while keying amounts (QA 2026-10-04): tax = taxable x rate, and the total
   * moves by the tax delta. Fees/interest/allowances are the backend's and refresh on save.
   */
  updateTotalAmountDue(): void {
    const rate = (this.returnData as any).taxRate;
    if (rate == null) {
      return;
    }
    this.returnData.totalTaxes = Math.round(this.returnData.taxableRentalReceipts * rate * 100) / 100;
    this.returnData.totalAmountDue =
      Math.round((this.loadedTotal + this.returnData.totalTaxes - this.loadedTaxes) * 100) / 100;
  }

  onFeesAllowancesUpdated(): void {
    this.loadReturnData();
  }

  exemptionTypes: ExemptionType[] = [];

  /** A claimed exemption needs its type; "Other" also needs an explanation. */
  exemptionProblem(): string | null {
    for (const p of this.returnData?.properties || []) {
      if (!(+p.exemptions > 0)) continue;
      const type = this.exemptionTypes.find(t => t.id === +(p.exemptionTypeId ?? 0));
      if (!type) return `Choose the exemption type for ${p.address}.`;
      if (type.name.toLowerCase() === 'other' && !(p.exemptionExplanation || '').trim()) {
        return `Explain the "Other" exemption for ${p.address}.`;
      }
    }
    return null;
  }

  save(): void {
    const problem = this.editMode ? this.exemptionProblem() : null;
    if (problem) {
      this.dialogService.openErrorDialog('Exemption type needed', problem);
      return;
    }
    this.saving = true;
    const updateDto: ReturnUpdateDto = {
      id: this.returnId,
      calculationDate: this.returnData.calculationDate,
      customerEditingEnabled: this.editMode,
      properties: (this.returnData.properties || []).map((p: any) => ({
        propertyId: p.propertyId ?? p.id,
        grossRentalReceipts: p.grossRentalReceipts,
        exemptions: p.exemptions,
        exemptionTypeId: p.exemptionTypeId ?? null,
        exemptionExplanation: p.exemptionExplanation || null
      })),
      expectedVersion: this.returnData.version as number
    };
    this.casheringService.updateReturn(this.returnId, updateDto).subscribe(
      () => {
        // Stay on the screen: reload the authoritative figures and keep editing.
        this.savedDirty = true;
        this.saving = false;
        this.loadReturnData();
      },
      (error: any) => {
        this.saving = false;
        console.error('Error saving return:', error);
        if (error?.status === 409) {
          // Stale form (someone else saved, or a paid/multi-payer guard): show and reload.
          this.dialogService.openErrorDialog('Unable to save',
            error?.error?.message || 'This return was changed by someone else — reload')
            .subscribe(() => this.loadReturnData());
        } else if (error?.error?.message) {
          this.dialogService.openErrorDialog('Unable to save', error.error.message);
        }
      }
    );
  }

  /** "Owner" / "Management Co" for the reportedByRole code. */
  get reportedByLabel(): string | null {
    const role = this.returnData?.reportedByRole;
    if (!role) { return null; }
    return role === 'MANAGEMENT_COMPANY' ? 'Management Co' : 'Owner';
  }

  /** Name of the account that filed, when it is one of the two we know by name. */
  get reportedByAccountName(): string | null {
    const d: any = this.returnData;
    if (!d?.reportedByAccountId) { return null; }
    if (d.reportedByAccountId === d.owningAccountId) { return d.owningAccountName ?? null; }
    if (d.reportedByAccountId === d.accountId) { return d.accountName ?? null; }
    return `Account #${d.reportedByAccountId}`;
  }

  printVoucher(): void {
    this.casheringService.printVoucher(this.returnId).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `voucher-${this.returnId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      (error: any) => {
        console.error('Error printing voucher:', error);
      }
    );
  }


  goBack(): void {
    if (this.isDialog) {
      this.dialogRef.close(this.savedDirty);
      return;
    }
    if (this.embedded) {
      this.back.emit(this.savedDirty);
      return;
    }
    this.router.navigate(['/app/search/customer', this.customerId]);
  }

  cancel(): void {
    this.goBack();
  }
}

/**
 * Taxable receipts = sum of each line's (gross - exemptions) clamped at 0, matching the backend:
 * one line's exemptions never reduce another line's tax.
 */
export function taxableReceipts(lines: { grossRentalReceipts: number; exemptions: number }[]): number {
  return lines.reduce((s, l) => s + Math.max(0, (+l.grossRentalReceipts || 0) - (+l.exemptions || 0)), 0);
}
